import React, { useEffect, useState } from "react";
import { EmployeeSidebar } from "./EmployeeDetails";
import { SideBarContentHeader } from "@/common/ui/SideBar";
import { LightText2 } from "@/common/ui/Text";
// import SingleSelect from "@/common/custom/SingleSelect";
import SelectCustomDate from "@/common/custom/SelectCustomDate";
import { ColumnDef } from "@tanstack/react-table";
import Separator, { Center } from "@/common/css";
import { Image } from "@/common/ui/Image";
import Button from "@/common/ui/Button";
import { AttachmentsSkeleton } from "@/common/ui/Loader";
import { useEmployeeInfo } from "@/services/employee.services";
import { useParams } from "react-router-dom";
import { ModalConfig, withModal } from "@/provider/withModalProvider";
import FilePreviewModal from "@/common/modal/FilePreviewModal";
import EmptyList from "@/components/common/EmptyList";
import { SearchBar } from "./Activities";
import AddAttachments from "@/components/employee/modal/AddAttachments";
import DeleteAttachment from "./modal/DeleteAttachment";
import RenderColumn from "@/components/employee/RenderColumn";
import CommonTableView from "@/components/common/table/CommonTableView";

interface AttachmentsProps {
  tab: EmployeeSidebar;
  openModal: (modalKey: string, props?: any) => void;
  activeModal: string;
}

const options1 = [
  { label: "All attachments", value: "all" },
  // { label: "Initial attachments", value: "initial" },
  // { label: "Meeting notes", value: "meeting_notes" },
];
const options2 = [
  { label: "All time", value: "all-time" },
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 days", value: "last7days" },
  { label: "Last 30 days", value: "last30days" },
  { label: "Custom period", value: "customPeriod" },
];

const modalConfig: ModalConfig = {
  filePreview: {
    component: FilePreviewModal,
    title: "Preview",
    type: "full",
    asChild: true,
  },
  AddAttachments: {
    component: AddAttachments,
    title: "Upload attachments",
    modalClassName: "!max-w-[400px]",
    type: "side",
  },
  DeleteAttachment: {
    component: DeleteAttachment,
    title: "Delete",
    type: "center",
    isQueryRequired: true,
  },
};

const MainComponent: React.FC<AttachmentsProps> = ({ tab, openModal, activeModal }) => {
  const { employeeId } = useParams();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [filter, setFilter] = useState({ type: options1[0], time: options2[0] });
  const { isLoading, data, refetch } = useEmployeeInfo({ empId: employeeId, view_typ: tab.apiKey, query: searchQuery, filter });

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  const handleTimeChange = (value: any) => {
    setFilter((pre) => ({ ...pre, time: value }));
  };

  const handleDownload = async (file: any) => {
    try {
      const response = await fetch(file.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const dynamicColumnsConfig: ColumnDef<any>[] = [
    {
      accessorKey: "name",
      header: "File name",
    },

    {
      accessorKey: "file_type",
      header: "File type",
    },
    {
      accessorKey: "size",
      header: "File size",
    },
    {
      accessorKey: "owner",
      header: "Uploaded by",
      cell: ({ row, column }: any) => <RenderColumn type={"profile"} row={row} column={column} />,
    },
    {
      accessorKey: "added_ts",
      header: "Uploaded on",
    },
  ];

  useEffect(() => {
    if (!activeModal) {
      setTimeout(() => {
        refetch();
      }, 100);
    }
  }, [activeModal]);

  return (
    <div className="flex flex-col w-full h-[calc(100%-102px)]">
      {tab?.name && (
        <SideBarContentHeader heading={`Attachments ${data?.total >= 0 ? `(${data.total})` : ""}`} className="!p-4 !z-0 rounded-llg rounded-b-none">
          <Button
            variant="outline"
            size="small"
            onClick={() => openModal("AddAttachments")}
            className=" border-text-primary border font-medium rounded-50 bg-primaryOpacity-5 text-text-primary"
            leftIcon={<Image src="/icons/file-upload-icon.svg" alt="" className=" h-4" />}
          >
            Add attachments
          </Button>
        </SideBarContentHeader>
      )}
      <div className="pb-2 w-full h-[calc(100%-16px)] ">
        <div className="flex items-center flex-wrap md:flex-nowrap py-4 px-3 gap-3 w-full h-20 mb-8 md:mb-4 ">
          {Object.keys(rowSelection)?.length > 0 ? (
            <div className="flex items-center gap-2 pt-2">
              <Button variant="outline-danger" size="small" onClick={() => {}}>
                Delete
              </Button>
              <Button variant="outline" size="small" className=" !border-icon-default2" onClick={() => setRowSelection({})}>
                Cancel
              </Button>
              <LightText2 text={`${Object.keys(rowSelection).length} files selected`} />
            </div>
          ) : (
            <>
              <LightText2 text="Filter by" className="text-13" />
              {/* <SingleSelect
                options={options1}
                placeholder="Select filter"
                onChange={handleTypeChange}
                selectedValue={filter.type}
                containerClass="flex item-center w-fit"
                textClassname="text-13"
                className="w-full text-13 leading-normal font-normal text-text-primary gap-1"
                dropdownClassName="w-[150px] top-9 z-[60]"
                listClassName="max-h-[300px] overflow-y-auto text-text-default bg-white "
                activeClass="text-text-primary"
              />
              <Separator isVertical className=" bg-fieldBorder h-1/2" /> */}
              <SelectCustomDate options={options2} placeholder="Choose a date range" onChange={handleTimeChange} selectedValue={filter.time} />
              <Separator isVertical className=" bg-fieldBorder h-1/2 hidden md:block" />
              <div className="w-full md:w-auto">
                <SearchBar onSearch={(q: string) => setSearchQuery(q)} query={searchQuery} placeholder=" Search attachments" className="w-full" />
              </div>
            </>
          )}
        </div>
        {/* Table View */}
        {isLoading ? (
          <div className="flex h-[calc(100%-44px)] items-center justify-center">
            <AttachmentsSkeleton />
          </div>
        ) : data?.rows?.length > 0 ? (
          <CommonTableView
            className="px-2 h-[calc(100%-68px)]"
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            data={data.rows}
            columnsConfig={dynamicColumnsConfig}
            setSelectedRows={(rows) => console.log(rows)}
            openModal={(modalKey, props) => console.log(modalKey, props)}
            paginationConfig={{ initialPage: 1, initialPerPage: 25, perPageOptions: [10, 25, 50, 100] }}
            dropdownOptions={[
              {
                label: "Preview",
                onClickFun: (data) => openModal("filePreview", { file: { name: data?.name, url: data?.link } }),
              },
              {
                label: "Download",
                onClickFun: (data) => handleDownload({ name: data?.name, url: data?.link }),
              },
              {
                label: "Delete",
                type: "remove",
                onClickFun: (data) => openModal("DeleteAttachment", { data }),
              },
            ]}
            linkColumns={[{ id: "name", url: ":identifier", identifier: "link", isExternal: true }]}
          />
        ) : (
          <Center className="h-full">
            <EmptyList
              text={searchQuery ? "Attachments not Found!" : "You don’t have any files yet!"}
              icon="/icons/empty-list-file.svg"
              className="h-full gap-y-4"
            />
          </Center>
        )}
      </div>
    </div>
  );
};

export const Attachments = withModal(MainComponent, modalConfig);
